import { useState } from 'react';
import { api, ApiError } from '../api';

export const SUB_ACCOUNT_SCOPES = [
  'View Contacts',
  'View Conversations',
  'View Conversation Messages',
  'View Calendars',
  'View Calendar Events',
  'View Users',
  'View Locations',
];

export const AGENCY_SCOPES = ['View Locations', 'Edit Locations'];

/**
 * Connects one sub-account with its own Private Integration Token.
 *
 * This is the path that needs no marketplace app at all: the token is created
 * inside the sub-account, and HighLevel accepts it on every endpoint this
 * dashboard reads for that sub-account.
 */
export function ConnectSubAccountForm({ onConnected }: { onConnected: () => void }) {
  const [locationId, setLocationId] = useState('');
  const [token, setToken] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const result = await api.connectLocation({
        locationId: locationId.trim(),
        token: token.trim(),
        label: label.trim() || undefined,
      });
      setDone(result.name ?? result.locationId);
      setLocationId('');
      setToken('');
      setLabel('');
      onConnected();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="stack">
      <div className="dialog-grid">
        <div className="field">
          <label htmlFor="pit-location">Sub-account ID *</label>
          <input
            id="pit-location"
            type="text"
            required
            placeholder="ve9EPM428h8vShlRW1KT"
            value={locationId}
            onChange={(event) => setLocationId(event.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="pit-label">Label (optional)</label>
          <input
            id="pit-label"
            type="text"
            placeholder="Acme Mortgage"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
          />
        </div>
        <div className="field full">
          <label htmlFor="pit-token">Private Integration Token *</label>
          <input
            id="pit-token"
            type="password"
            required
            autoComplete="off"
            placeholder="pit-…"
            value={token}
            onChange={(event) => setToken(event.target.value)}
          />
        </div>
      </div>

      <p className="small muted">
        In the sub-account: <strong>Settings → Private Integrations → Create new integration</strong>.
        Tick {SUB_ACCOUNT_SCOPES.join(', ')}. The sub-account ID is the long string in the HighLevel
        URL after <code>/location/</code>.
      </p>

      {error ? <div className="banner error">{error}</div> : null}
      {done ? <div className="banner">Connected {done}. Syncing has started.</div> : null}

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="submit" className="btn btn-primary" disabled={busy || !locationId.trim() || !token.trim()}>
          {busy ? 'Verifying…' : 'Connect sub-account'}
        </button>
      </div>
    </form>
  );
}

/**
 * Connects the agency, which adds automatic sub-account discovery and the
 * ability to create a sub-account — both agency-only in HighLevel.
 */
export function ConnectAgencyForm({ onConnected }: { onConnected: () => void }) {
  const [companyId, setCompanyId] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setDone(false);
    try {
      await api.connectAgency({ companyId: companyId.trim(), token: token.trim() });
      setDone(true);
      setCompanyId('');
      setToken('');
      onConnected();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : String(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="stack">
      <div className="dialog-grid">
        <div className="field">
          <label htmlFor="agency-id">Agency ID *</label>
          <input
            id="agency-id"
            type="text"
            required
            placeholder="tDtDnQdgm2LXpyiqYvZ6"
            value={companyId}
            onChange={(event) => setCompanyId(event.target.value)}
          />
        </div>
        <div className="field full">
          <label htmlFor="agency-token">Agency Private Integration Token *</label>
          <input
            id="agency-token"
            type="password"
            required
            autoComplete="off"
            placeholder="pit-…"
            value={token}
            onChange={(event) => setToken(event.target.value)}
          />
        </div>
      </div>

      <p className="small muted">
        At agency level: <strong>Settings → Private Integrations</strong>. Tick{' '}
        {AGENCY_SCOPES.join(' and ')}. The agency ID is under Settings → Company.
      </p>

      {error ? <div className="banner error">{error}</div> : null}
      {done ? <div className="banner">Agency connected. Pulling your sub-account list now.</div> : null}

      <div className="row" style={{ justifyContent: 'flex-end' }}>
        <button type="submit" className="btn btn-primary" disabled={busy || !companyId.trim() || !token.trim()}>
          {busy ? 'Verifying…' : 'Connect agency'}
        </button>
      </div>
    </form>
  );
}
